/** Request shape for the demo assistant. */
interface AssistantChatRequest {
  message: string;
}

/** Response shape returned by the demo assistant. */
interface AssistantChatResponse {
  id: string;
  content: string;
  timestamp: string;
  toolCall?: {
    id: string;
    name: string;
    status: string;
    result?: unknown;
  };
  uiBlocks?: Array<{
    id: string;
    component: string;
    props: Record<string, unknown>;
  }>;
  toolsUsed?: string[];
}

const DEMO_RESPONSES: Record<string, AssistantChatResponse> = {
  'overdue': {
    id: 'demo-1',
    content: 'Here\'s a summary of overdue accounts:\n\n- **0-30 days**: 45 accounts (£125,000)\n- **31-60 days**: 28 accounts (£85,000)\n- **61-90 days**: 12 accounts (£42,000)\n- **90+ days**: 8 accounts (£31,000)\n\nWould you like me to show details for a specific ageing band or prioritise by amount?',
    timestamp: new Date().toISOString(),
    toolCall: {
      id: 'tool-1',
      name: 'Get Overdue Summary',
      status: 'success',
      result: { totalOverdue: 93, totalAmount: 283000 },
    },
  },
  'collections': {
    id: 'demo-2',
    content: 'Collection queue summary:\n\n- **Active cases**: 34\n- **Promises to pay**: 12 (£45,000 expected)\n- **Broken promises**: 3\n- **Cases requiring action**: 8\n\nWould you like me to show the priority queue or details of cases requiring immediate action?',
    timestamp: new Date().toISOString(),
    toolCall: {
      id: 'tool-2',
      name: 'Get Collection Summary',
      status: 'success',
      result: { activeCases: 34, promisesToPay: 12 },
    },
  },
  'disputes': {
    id: 'demo-3',
    content: 'Dispute overview:\n\n- **Open disputes**: 15 (£67,000)\n- **Under investigation**: 8\n- **Awaiting customer response**: 4\n- **Resolution pending**: 3\n- **Average resolution time**: 12 days\n\nWould you like to see details for any specific dispute status?',
    timestamp: new Date().toISOString(),
    toolCall: {
      id: 'tool-3',
      name: 'Get Dispute Summary',
      status: 'success',
      result: { openDisputes: 15, totalAmount: 67000 },
    },
  },
  'aging': {
    id: 'demo-4',
    content: 'Ageing analysis for all companies:\n\n| Band | Accounts | Amount | % of Total |\n|------|----------|--------|------------|\n| Current | 245 | £1,250,000 | 65% |\n| 1-30 days | 45 | £125,000 | 15% |\n| 31-60 days | 28 | £85,000 | 10% |\n| 61-90 days | 12 | £42,000 | 5% |\n| 90+ days | 8 | £31,000 | 5% |\n\nWould you like to drill down into a specific band?',
    timestamp: new Date().toISOString(),
  },
  'payments': {
    id: 'demo-5',
    content: 'Payment activity summary:\n\n- **Receipts today**: 12 (£34,500)\n- **Unallocated payments**: 5 (£8,200)\n- **Failed direct debits**: 2 (£1,400)\n- **Pending allocations**: 7\n\nWould you like me to show unallocated payments or recent receipts?',
    timestamp: new Date().toISOString(),
    toolCall: {
      id: 'tool-5',
      name: 'Get Payment Summary',
      status: 'success',
      result: { receiptsToday: 12, totalReceived: 34500 },
    },
  },
  'default': {
    id: 'demo-default',
    content: 'I\'m the Credit Control Assistant. I can help you with:\n\n- **Overdue Accounts**: View ageing analysis and overdue summaries\n- **Collections**: Manage collection cases and promises to pay\n- **Disputes**: Track and resolve customer disputes\n- **Payments**: View receipts, allocations, and payment status\n- **Credit Scoring**: Assess customer credit risk\n- **Reports**: Generate summaries and analytics\n\nWhat would you like to know?',
    timestamp: new Date().toISOString(),
  },
};

function findBestMatch(message: string): AssistantChatResponse {
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('overdue') || lowerMessage.includes('outstanding')) {
    return { ...DEMO_RESPONSES['overdue'], id: generateId(), timestamp: new Date().toISOString() };
  }
  if (lowerMessage.includes('collection') || lowerMessage.includes('queue') || lowerMessage.includes('case')) {
    return { ...DEMO_RESPONSES['collections'], id: generateId(), timestamp: new Date().toISOString() };
  }
  if (lowerMessage.includes('dispute')) {
    return { ...DEMO_RESPONSES['disputes'], id: generateId(), timestamp: new Date().toISOString() };
  }
  if (lowerMessage.includes('aging') || lowerMessage.includes('ageing') || lowerMessage.includes('age analysis')) {
    return { ...DEMO_RESPONSES['aging'], id: generateId(), timestamp: new Date().toISOString() };
  }
  if (lowerMessage.includes('payment') || lowerMessage.includes('receipt') || lowerMessage.includes('allocation')) {
    return { ...DEMO_RESPONSES['payments'], id: generateId(), timestamp: new Date().toISOString() };
  }

  return { ...DEMO_RESPONSES['default'], id: generateId(), timestamp: new Date().toISOString() };
}

function generateId(): string {
  return `demo-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

class AIAssistantDemoService {
  async sendMessage(request: AssistantChatRequest, signal?: AbortSignal): Promise<AssistantChatResponse> {
    await this.simulateDelay(signal);
    return findBestMatch(request.message);
  }

  private simulateDelay(signal?: AbortSignal): Promise<void> {
    const delay = 500 + Math.random() * 1000;
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(resolve, delay);
      if (signal) {
        signal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(new DOMException('Aborted', 'AbortError'));
        });
      }
    });
  }
}

export const aiAssistantDemoService = new AIAssistantDemoService();
